import { PERCENT_TO_MG_PER_GRAM } from './constants.js';

/**
 * Pure unit/precision helpers. Everything that touches "measurable" weights
 * works in INTEGER scale units to avoid binary floating-point drift.
 *
 * A "scale unit" is one step of the scale. For a 0.01 g scale, 1 unit = 0.01 g,
 * so 0.25 g = 25 units and 7 units = 0.07 g.
 */

/** Small epsilon for tolerating float noise when checking divisibility. */
const EPSILON = 1e-9;

/**
 * Number of decimal places implied by a scale precision.
 *   0.1   -> 1
 *   0.01  -> 2
 *   0.001 -> 3
 */
export function decimalsForPrecision(precisionGrams: number): number {
  if (precisionGrams <= 0) {
    throw new Error('Scale precision must be greater than zero');
  }
  // Derive from a base-10 logarithm, then clamp to a sane range.
  const decimals = Math.round(-Math.log10(precisionGrams));
  return Math.max(0, Math.min(6, decimals));
}

/**
 * Convert a weight in grams to integer scale units, given a precision.
 * Throws if the weight is not an exact multiple of the precision.
 */
export function gramsToUnits(grams: number, precisionGrams: number): number {
  const raw = grams / precisionGrams;
  const rounded = Math.round(raw);
  if (Math.abs(raw - rounded) > EPSILON * Math.max(1, Math.abs(raw))) {
    throw new Error(
      `Weight ${grams} g is not measurable at ${precisionGrams} g precision`,
    );
  }
  return rounded;
}

/** Convert integer scale units to grams. */
export function unitsToGrams(units: number, precisionGrams: number): number {
  // Round to the precision's decimal count to keep the value clean.
  const decimals = decimalsForPrecision(precisionGrams);
  const grams = units * precisionGrams;
  const factor = 10 ** decimals;
  return Math.round(grams * factor) / factor;
}

/**
 * Whether a weight in grams is exactly measurable at the given precision.
 */
export function isMeasurable(grams: number, precisionGrams: number): boolean {
  const raw = grams / precisionGrams;
  const rounded = Math.round(raw);
  return Math.abs(raw - rounded) <= EPSILON * Math.max(1, Math.abs(raw));
}

/**
 * Nearest measurable weights (lower and higher) around a target that is not
 * exactly measurable. Returns exact multiples of the precision.
 */
export function nearestMeasurableWeights(
  grams: number,
  precisionGrams: number,
): { lower: number; higher: number } {
  const decimals = decimalsForPrecision(precisionGrams);
  const factor = 10 ** decimals;
  const lowerUnits = Math.floor(grams / precisionGrams + EPSILON);
  const higherUnits = lowerUnits + 1;
  return {
    lower: Math.round(lowerUnits * precisionGrams * factor) / factor,
    higher: Math.round(higherUnits * precisionGrams * factor) / factor,
  };
}

/**
 * Theoretical THC in mg for a given weight (grams) and THC percentage.
 *   mg = grams × percent × 10
 */
export function thcMgFromGrams(grams: number, thcPercent: number): number {
  return grams * thcPercent * PERCENT_TO_MG_PER_GRAM;
}

/** Convert THC percent to mg per gram (percent × 10). */
export function percentToMgPerGram(percent: number): number {
  return percent * PERCENT_TO_MG_PER_GRAM;
}

/** Convert THC mg per gram to percent (mg/g ÷ 10). */
export function mgPerGramToPercent(mgPerGram: number): number {
  return mgPerGram / PERCENT_TO_MG_PER_GRAM;
}

/** Format a gram value using the decimals implied by the precision. */
export function formatGrams(grams: number, precisionGrams: number): string {
  return grams.toFixed(decimalsForPrecision(precisionGrams));
}

/** Round a number to a fixed number of decimals (returns a number). */
export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}
